import {
  getPendingWindowsEVRequestName,
  getWindowsEVRequestReset,
} from '../util/windowsEVRequest';
import { UploadTask } from 'firebase/storage';
import { deleteField, writeBatch } from 'firebase/firestore';

import type { FnInput } from '../../../functions';
import {
  CustomLinuxPGPKey,
  CustomMacCodeSign,
  CustomNotarizationApiKeyAuth,
  CustomNotarizationPasswordHsmAuth,
  CustomWindowsCodeSignEV,
  CustomWindowsCodeSignFile,
  getInstalledWindowsCertName,
  getRetiredCertificateLocalMeta,
  IApp,
  IAppFromServerProps,
  IUser,
  IWindowOptions,
  MacTarget,
  PortalConfigKey,
  WindowsEVOnboardingProvider,
  WindowsTarget,
} from '@todesktop/shared';
import {
  createElectronApp,
  createV2App,
  CreateNewAppProps,
  DeepPartial,
  defaultIcon,
} from '../types';
import merge from 'lodash.merge';
import {
  update,
  store,
  selectedApp,
  IGlobalState,
  AppUser,
  selectedAppUser,
} from '../../store';
import { createAsyncAction, successResult, errorResult } from 'pullstate';
import {
  updateUserApplicationData,
  getUserAppDoc,
  getUserDeletedAppDoc,
} from '../firebase';
import { goToCreateNewAppModal, updateUIState } from './uistate';
import { history, IAppFieldsContext } from '../util';
import { track } from '../util/analytics';
import { message, Modal } from 'antd';
import {
  resetBuildSubscriptions,
  subscribeToBuildCollectionUpdates,
} from './builds';
import { UploadFile } from 'antd/lib/upload/interface';
import { getAccountDisplayName } from '../util/accountDisplayName';
import { callFirebaseFunction } from '../firebase/functions';
import { firestore } from '../firebase/firestore';
import { stage } from '../firebase/config';
import { v4 } from 'uuid';
import { getLinuxPgpMetadataUrl } from '../util/urls';
import { toError } from '../util/errors';
import { getGoogleAdsAttribution } from '../util/googleAdsAttribution';

export type LegacyCustomMacCodeSign = Exclude<
  CustomMacCodeSign,
  { type: 'hsm' }
>;

const createApplication = callFirebaseFunction('createApplication');

export const selectApp = ({ id }: IAppFromServerProps | IApp) => {
  // Clear the previous builds state first, so the page never sees the new app
  // with it
  resetBuildSubscriptions();
  update((draft) => {
    draft.selectedApp = id;
  });

  subscribeToBuildCollectionUpdates(id);
};

export const addApp = (app: IApp, owner: AppUser) => addApps([app], owner);

/** Adds an account's apps in one update, so pages re-render once per account */
export const addApps = (apps: IApp[], owner: AppUser) => {
  const existingIds = new Set(store.getRawState().apps.map(({ id }) => id));
  const newApps = apps.filter((app) => !existingIds.has(app.id));
  if (!newApps.length) return;

  update((draft) => {
    for (const app of newApps) {
      // use placeholder icon if no icon present
      if (!app.icon) {
        app.icon = defaultIcon;
      }
      draft.apps.push(app);
      draft.appToUser[app.id] = owner;
    }
  });
};

export const addNewApp = async (
  props: CreateNewAppProps,
  creationRequestId?: string,
) => {
  const { user, createAppAccountId, acceptedUsers } = store.getRawState();
  let app = createElectronApp(props);

  if (!user) throw new Error('You must be logged in to create an app.');

  // A team the person runs, or their own account.
  const accountId = createAppAccountId || user.id;
  const team = acceptedUsers.find((item) => item.id === accountId);
  const saveResult = await saveApplication.run({
    id: accountId,
    app,
    creationRequestId,
  });
  if (saveResult.error) {
    throw new Error(saveResult.message || 'Could not save the app.');
  }
  app = saveResult.payload?.app || app;
  addApp(
    app,
    accountId === user.id
      ? { id: user.id, label: user.firstName ?? '', isOwner: true }
      : {
          id: accountId,
          label: team ? getAccountDisplayName(team) : accountId,
          isOwner: false,
        },
  );
  update((draft) => {
    draft.createAppAccountId = null;
  });

  selectApp(app);
};

const v2AppSetups = new Map<string, Promise<void>>();

/**
 * Has ToDesktop make a v2 app's update and Linux repository keys
 * (setUpV2App), one call at a time per app in this tab. It takes a while
 * (Key Vault makes each key), so creating an app doesn't wait for it; the
 * app's pages call it again while the keys are missing (useV2AppKeys).
 */
export const setUpV2AppKeys = (appId: string): Promise<void> => {
  let setup = v2AppSetups.get(appId);
  if (!setup) {
    setup = callFirebaseFunction('setUpV2App')({ appId })
      .then(() => undefined)
      .finally(() => v2AppSetups.delete(appId));
    v2AppSetups.set(appId, setup);
  }
  return setup;
};

/**
 * Creates a v2 app, and starts ToDesktop making its keys without waiting
 * for them (setUpV2AppKeys).
 */
export const addNewV2App = async (
  props: { id: string; name: string },
  creationRequestId?: string,
) => {
  const { user, createAppAccountId, acceptedUsers } = store.getRawState();
  if (!user) throw new Error('You must be logged in to create an app.');
  // A team the person runs, or their own account (as addNewApp).
  const accountId = createAppAccountId || user.id;
  const team = acceptedUsers.find((item) => item.id === accountId);
  let app = createV2App(props);
  const saveResult = await saveApplication.run({
    app,
    creationRequestId,
    id: accountId,
  });
  if (saveResult.error) {
    throw new Error(saveResult.message || 'Could not save the app.');
  }
  app = saveResult.payload?.app || app;
  addApp(
    app,
    accountId === user.id
      ? { id: user.id, isOwner: true, label: user.firstName ?? '' }
      : {
          id: accountId,
          isOwner: false,
          label: team ? getAccountDisplayName(team) : accountId,
        },
  );
  update((draft) => {
    draft.createAppAccountId = null;
  });
  selectApp(app);
  // The app's pages show the keys being made, and try again if this fails.
  setUpV2AppKeys(props.id).catch(() => undefined);
};

export const saveApplication = createAsyncAction<
  {
    id: string;
    app: IApp;
    creationRequestId?: string;
  },
  { app?: IApp }
>(async ({ id, app, creationRequestId }) => {
  try {
    const ownId = store.getRawState().user?.id;
    const response = await createApplication({
      app,
      // Apps go in the person's own account unless they name a team.
      ...(id && ownId && id !== ownId ? { accountId: id } : {}),
      ...(creationRequestId ? { creationRequestId } : {}),
    });

    return successResult({ app: response?.data?.app });
  } catch (error) {
    return errorResult(
      [],
      toError(error, 'An error occurred while saving the app').message,
    );
  }
});

export const updateFirestoreApp = async (
  props: DeepPartial<IApp>,
  appHasChanged = true,
) => {
  const state = store.getRawState();
  const currentApp = selectedApp(state);
  const id = currentApp?.id;
  if (!id) {
    throw new Error('Tried updating selected app but no app is selected');
  }
  if (state && state.user && state.user.id) {
    const app = currentApp;
    const user = selectedAppUser(state);

    if (app && app.meta && appHasChanged && !app.meta.isAppChanged) {
      await updateUserApplicationData(user.id, id, {
        meta: {
          isAppChanged: true,
        },
      });
    }
    await updateUserApplicationData(user.id, id, props);
  } else {
    updateAppWithMerge(props);
  }
};

export const updateAndDispatchApp = async (
  appId: string,
  props: DeepPartial<IApp>,
) => {
  const state = store.getRawState();
  const user = state.appToUser[appId];

  await updateUserApplicationData(user.id, appId, props);
  update((draft) => {
    const app = draft.apps.find((a) => a.id === appId);
    if (app) merge(app, props);
  });
};

export const updateWindowOptions = (props: DeepPartial<IWindowOptions>) => {
  return updateFirestoreApp({ windowOptions: props } as any);
};

export const updateAppWithMerge = (props: DeepPartial<IApp>) => {
  update((draft) => {
    const app = selectedApp(draft);
    merge(app, props);
  });
};

export const changeOSPreview = (newOs: IGlobalState['previewScreenshotOS']) => {
  update((draft) => {
    draft.previewScreenshotOS = newOs;
  });
};

export const updateAppLiveField =
  (field: string) => (value: string | number | undefined) => {
    const id = selectedApp(store.getRawState())?.id;
    update((draft) => {
      draft.liveFields[`${id}-${field}`] = value;
    });
  };

export const setShouldCreateSubWithoutBuild = (status: boolean) => {
  update((draft) => {
    draft.shouldCreateSubWithoutBuild = status;
  });
};

/** Opens app creation, in a team the person runs or their own account. */
export const newApp = (accountId?: string) => {
  track({ event: 'Create New App Modal' });
  goToCreateNewAppModal(accountId);
};

export const deleteApp = async (user: IUser, app: IApp) => {
  // A team's Admins delete apps from the team, not their own account.
  const accountId = store.getRawState().appToUser[app.id]?.id || user.id;
  const batch = writeBatch(firestore);
  batch.set(getUserDeletedAppDoc(accountId, app.id), app);
  batch.delete(getUserAppDoc(accountId, app.id));
  await batch.commit();

  history.push('/');

  const state = store.getRawState();
  const currentSelectedApp = selectedApp(state);
  const currentAppIndex = currentSelectedApp
    ? state.apps.indexOf(currentSelectedApp)
    : -1;

  update((draft) => {
    if (currentAppIndex >= 0) {
      draft.apps.splice(currentAppIndex, 1);
    }
    delete draft.appToUser[app.id];
    draft.selectedApp = draft.apps[0]?.id ?? null;
    draft.uiState = null;
  });
};

const setCodeSigningCert = callFirebaseFunction('setCodeSigningCert');

// Certificate references are written only by setCodeSigningCert, which checks
// the certificate was created for the app and, in the same transaction,
// retires the replaced certificate's request. Show the fields it saved.
const saveCodeSigningCert = async (input: FnInput<'setCodeSigningCert'>) => {
  const {
    data: { fields, retired },
  } = await setCodeSigningCert(input);
  update((draft) => {
    const current = draft.apps.find(
      (candidate) => candidate.id === input.appId,
    );
    if (!current) return;
    for (const key of codeSigningCertKeys) {
      if (fields[key] === undefined) delete current[key];
      else Object.assign(current, { [key]: fields[key] });
    }
    merge(current, {
      meta: {
        isAppChanged: true,
        ...(retired ? getRetiredCertificateLocalMeta(retired, current) : {}),
      },
    });
  });
};

const codeSigningCertKeys = [
  'customMacCodeSign',
  'customNotarization',
  'customWindowsCodeSign',
  'macHsmCertNames',
  'windowsHsmCertNames',
] as const;

export const updateWindowsCert = (
  app: IApp,
  target: WindowsTarget,
  customWindowsCodeSign: CustomWindowsCodeSignFile | CustomWindowsCodeSignEV,
) =>
  saveCodeSigningCert({
    appId: app.id,
    platform: 'windows',
    target,
    hsmCertName: customWindowsCodeSign.hsmCertName,
    hsmCertType: customWindowsCodeSign.hsmCertType,
  });

const addAppSecret = callFirebaseFunction('addAppSecret');
export const updateLinuxPgpKey = async (
  app: IApp,
  ascString: string,
  password: string,
) => {
  const uuid = app.customLinuxPgpKey?.$key
    ? app.customLinuxPgpKey.$key.slice(-6)
    : v4().slice(-6);

  const keyRef = (
    (
      await addAppSecret({
        appId: app.id,
        key: `linux-pgp-key-${uuid}`,
        value: ascString,
      })
    ).data as { name: string }
  ).name;
  let passwordRef = null;
  if (password && password.length > 0) {
    passwordRef = (
      (
        await addAppSecret({
          appId: app.id,
          key: `linux-pgp-pass-${uuid}`,
          value: password,
        })
      ).data as { name: string }
    ).name;
  }

  const customLinuxPgpKey = {
    $key: keyRef,
  } as CustomLinuxPGPKey;

  if (passwordRef) {
    customLinuxPgpKey.$password = passwordRef;
  }

  await updateFirestoreApp({
    customLinuxPgpKey,
  });

  const linuxPgpMetadataUrl = getLinuxPgpMetadataUrl(app.id);

  const linuxPgpMetadata = await fetch(linuxPgpMetadataUrl).then((res) =>
    res.json(),
  );

  if (linuxPgpMetadata?.error) {
    await updateFirestoreApp({
      customLinuxPgpKey: deleteField() as any,
    });
    Modal.error({
      title: 'Error adding Linux PGP key',
      content: linuxPgpMetadata.error,
    });
    return;
  }
  message.success('PGP key saved');
};

export const updateMacCert = async (
  app: IApp,
  password: string,
  pfxBase64: string,
  target: MacTarget,
) => {
  const addStandardCodeSigningCert = callFirebaseFunction(
    'addStandardCodeSigningCert',
  );

  const { data } = await addStandardCodeSigningCert({
    appId: app.id,
    password,
    pfxBase64,
  });

  const typedData = data as { hsmCertName: string };

  try {
    await deleteMacCertificate(app, target);
  } catch {
    // This is just house keeping, we shouldn't block assigning of the new cert.
  }

  await saveCodeSigningCert({
    appId: app.id,
    platform: 'mac',
    target,
    hsmCertName: typedData.hsmCertName,
  });
};

export const updateMacNotarization = async (
  appId: string,
  secret: string,
  notarizationData:
    | Omit<CustomNotarizationApiKeyAuth, '$appleApiKey'>
    | Omit<CustomNotarizationPasswordHsmAuth, '$appSpecificPassword'>,
) => {
  switch (notarizationData.type) {
    case 'hsm': {
      const customNotarization: DeepPartial<CustomNotarizationPasswordHsmAuth> =
        {
          type: 'hsm',
          appleId: notarizationData.appleId,
          teamId: notarizationData.teamId,
        };

      if (typeof secret === 'string' && secret.length > 0) {
        customNotarization.$appSpecificPassword = await addHSMSecret(
          appId,
          'mac-notarization-appSpecificPassword',
          secret,
        );
      }

      return await updateFirestoreApp({ customNotarization });
    }
    case 'hsm-api': {
      const customNotarization: DeepPartial<CustomNotarizationApiKeyAuth> = {
        type: 'hsm-api',
        appleApiKeyId: notarizationData.appleApiKeyId,
        appleApiIssuer: notarizationData.appleApiIssuer,
      };

      if (typeof secret === 'string' && secret.length > 0) {
        customNotarization.$appleApiKey = await addHSMSecret(
          appId,
          'mac-api-key',
          secret,
        );
      }

      return await updateFirestoreApp({
        customNotarization: {
          type: 'hsm-api',
          appleApiKeyId: notarizationData.appleApiKeyId,
          appleApiIssuer: notarizationData.appleApiIssuer,
          $appleApiKey: await addHSMSecret(appId, 'mac-api-key', secret),
        },
      });
    }
    default: {
      throw new Error('Invalid notarization type');
    }
  }
};

const deleteHSMCert = callFirebaseFunction('deleteHSMCert');

export const deleteMacCertificate = async (app: IApp, target: MacTarget) => {
  const hsmCertName = app?.macHsmCertNames?.[target];

  if (hsmCertName) {
    await deleteHSMCert({ appId: app.id, hsmCertName });
  }

  // Removing the main certificate also clears notarization.
  await saveCodeSigningCert({
    appId: app.id,
    platform: 'mac',
    target,
    hsmCertName: null,
  });
};

const deleteAppSecret = callFirebaseFunction('deleteAppSecret');

export const deleteLinuxPgpKey = async (app: IApp) => {
  const linuxPgpKeyName = app?.customLinuxPgpKey?.$key;
  const linuxPgpPasswordName = app?.customLinuxPgpKey?.$password;
  if (linuxPgpKeyName) {
    await deleteAppSecret({ appId: app.id, key: linuxPgpKeyName });
    if (linuxPgpPasswordName) {
      await deleteAppSecret({
        appId: app.id,
        key: linuxPgpPasswordName,
      });
    }
    await updateFirestoreApp({
      customLinuxPgpKey: deleteField() as any,
    });
  }
};

export const deleteWindowsCertificate = async (
  app: IApp,
  target: WindowsTarget,
) => {
  const signingConfig = app.customWindowsCodeSign;
  const hsmCertName = getInstalledWindowsCertName(app, target);
  // Azure setup can leave a historical HSM mapping behind. Retain that
  // reference, but only delete a resource used by active HSM signing.
  if (hsmCertName && signingConfig?.certType === 'hsm') {
    await deleteHSMCert({ appId: app.id, hsmCertName });
  }
  await saveCodeSigningCert({
    appId: app.id,
    platform: 'windows',
    target,
    hsmCertName: null,
  });
};

export const updateWindowsEVOnboardingProvider = (
  provider: WindowsEVOnboardingProvider,
) => {
  updateFirestoreApp(
    {
      meta: {
        // don't reset the tempHSMCertName or hasGeneratedCert step
        // if they choose to change the provider
        windowsEVOnboarding: {
          provider,
          hasChosenProvider: false,
          hasOrderedCert: false,
          hasBeenVerified: false,
          hasUploadedCert: false,
        },
      },
    },
    false,
  );
};

export const beginReplacingWindowsCert = async () => {
  const app = selectedApp(store.getRawState());
  if (!app) throw new Error('No app selected');
  if (getPendingWindowsEVRequestName(app)) return;
  await updateAndDispatchApp(app.id, {
    meta: { windowsEVOnboarding: getWindowsEVRequestReset(app) },
  });
};

export const updateWindowsEVOnboardingStep = (id: string, checked: boolean) =>
  updateFirestoreApp(
    {
      meta: {
        windowsEVOnboarding: {
          [id]: checked,
        },
      },
    },
    false,
  );

export const beginBuildProcess = async (
  context: IAppFieldsContext,
  eventProperties: { [key: string]: any } = {},
) => {
  if (await context.verifyFields()) {
    setShouldCreateSubWithoutBuild(false);
    updateUIState('choose-plan');
    track({ event: 'Began New Build Process', properties: eventProperties });
  } else {
    message.error(
      'Please fix the errors (highlighted in red) before building your app',
    );
  }
};

export const handleFileChange =
  (uploadCallback: (params: unknown) => void, uploadTask?: UploadTask) =>
  (params: unknown) => {
    uploadCallback(params);

    return {
      abort: () => {
        if (uploadTask) {
          abandonFileUpload(uploadTask);
        }
      },
    };
  };

export const isFileChangeError = (file: UploadFile) => {
  const { error } =
    file.response && file.response.files && file.response.files[0]
      ? file.response.files[0]
      : { error: undefined };

  if (error) {
    message.error(error, 3);
    return true;
  }

  return false;
};

export const isFileUploadError = (error: unknown, onError: any) => {
  const normalizedError = toError(error);
  if (error) {
    message.error(normalizedError.message, 3);
    onError(normalizedError);
  }
};

export const abandonFileUpload = (firebaseUploadTask: UploadTask) => {
  if (firebaseUploadTask && firebaseUploadTask.cancel) {
    firebaseUploadTask.cancel();
  }
};

export const toBase64 = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) =>
      resolve(btoa((event.target as FileReader).result as string));
    reader.onerror = (event) => reject((event.target as FileReader).error);
    reader.readAsBinaryString(file);
  });

export const toText = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

interface CustomerPortalProps {
  configuration?: PortalConfigKey; // todo: replace with enum
  returnUrl?: string;
  flowData?: {
    type: 'subscription_update';
    subscription_update: {
      subscription: string;
    };
  };
  /** Account ID to manage billing for. If not provided, uses the authenticated user's account. */
  accountId?: string;
}

export async function createCustomerPortalSession(
  props: CustomerPortalProps = {},
) {
  const sessionUrl = await callFirebaseFunction('createCustomerPortalSession')({
    returnUrl: window.location.href,
    ...props,
  });

  window.location.replace(
    (sessionUrl.data as { url: string }).url || (sessionUrl.data as string),
  );
}

interface StripeCheckoutProps {
  isAppSetup?: boolean;
  shouldUseExistingPaymentMethod?: boolean;
  successUrl?: string;
  cancelUrl?: string;
  priceId: string;
  appId: string;
}

export function getPendingCheckoutPriceId(appId: string): string | undefined {
  try {
    return sessionStorage.getItem(`app-setup-price:${appId}`) || undefined;
  } catch {
    return undefined;
  }
}

export async function createCustomerCheckoutSession(
  props: StripeCheckoutProps,
) {
  const { isAppSetup, ...checkoutProps } = props;
  const appUrl = `${window.location.origin}/apps/${props.appId}`;
  if (isAppSetup) {
    try {
      sessionStorage.setItem(`app-setup-price:${props.appId}`, props.priceId);
    } catch {
      // Checkout still works when browser storage is unavailable.
    }
  }
  const sessionUrl = await callFirebaseFunction('createStripeCheckoutSession')({
    successUrl: window.location.href,
    cancelUrl: window.location.href,
    shouldUseExistingPaymentMethod: true,
    googleAdsAttribution: getGoogleAdsAttribution(),
    ...checkoutProps,
    ...(isAppSetup
      ? { successUrl: `${appUrl}?checkout=success`, cancelUrl: appUrl }
      : {}),
  });

  if (sessionUrl.data === true) {
    if (!isAppSetup) message.success('Subscription successfully created');
  } else {
    window.location.replace(
      (sessionUrl.data as { url: string }).url || (sessionUrl.data as string),
    );
  }
  return sessionUrl.data === true;
}

export const addHSMSecret = async (
  appId: string,
  name: string,
  secret: string,
): Promise<string> => {
  const addAppSecretFn = callFirebaseFunction('addAppSecret');
  return (
    (
      await addAppSecretFn({
        appId,
        key: generateHSMSecretKey(name),
        value: secret,
      })
    ).data as { name: string }
  ).name;
};

const formatCertName = (str: string) => str.replace(/[^0-9a-zA-Z-]/g, '');
export const generateHSMSecretKey = (name: string) => {
  const randomId = Math.random().toString(36).substring(2, 9);
  return formatCertName(`${stage}-${name}-${randomId}`);
};

const ymdDate = () => {
  const date = new Date();
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .split('T')[0]
    .split('-')
    .join('')
    .substring(2);
};

export const generateId = () =>
  ymdDate() + Math.random().toString(36).substring(2, 9);
